import {
  SearchParameters,
  SearchTeam,
  SearchUserEnriched,
} from 'lib-search-types';
import { useCallback, useEffect } from 'react';

import { searchApi } from 'apis/search';
import {
  useMembersWithPresence,
  useOrganizationPresenceSubscription,
  usePresenceOverlaySync,
  usePresenceSearchResync,
} from 'hooks/usePresenceCacheSync';
import { useCurrentUser } from 'utils/user';

const SEARCH_PARAMS: SearchParameters<'users', true> = {
  scope: 'users',
  filters: {
    strategy: 'all',
    filters: [
      { field: 'user_type', operator: 'is_one', values: [{ value: 'member' }] },
    ],
  },
  page: 0,
  pageSize: 1000,
  enrichmentSpec: ['teams'],
};

export type UsePresenceMembersResult = {
  members: SearchUserEnriched[];
  teams: readonly SearchTeam[];
  isLoading: boolean;
  isError: boolean;
};

export const usePresenceMembers = (): UsePresenceMembersResult => {
  useOrganizationPresenceSubscription();

  const { user } = useCurrentUser();

  const [triggerSearch, { allMembers, teams, membersLoading, membersFailed }] =
    searchApi.useLazyReadSearchUsersEnrichedQuery({
      selectFromResult: ({ data, isLoading, isUninitialized, isError }) => {
        const allMembers = data?.data.results.map(({ result }) => result) ?? [];
        const me = allMembers.find(({ id }) => id === user.id);

        return {
          allMembers,
          teams: me?.teams?.filter((t) => !t.is_deactivated) ?? [],
          membersLoading: isLoading || isUninitialized,
          membersFailed: isError,
        };
      },
    });

  const refetchMembers = useCallback(() => {
    triggerSearch(SEARCH_PARAMS);
  }, [triggerSearch]);

  useEffect(refetchMembers, [refetchMembers]);
  usePresenceSearchResync(refetchMembers);

  usePresenceOverlaySync();
  const members = useMembersWithPresence(allMembers);

  return {
    members,
    teams,
    isLoading: membersLoading,
    isError: membersFailed,
  };
};
